/**
 * CoSingery — **分享能力探針**（2026-09-23；產品負責人「sharing still not working」）
 *
 * 為何要：分享「唔 work」可以係**三層**任何一層出事——
 *   ① 平台根本冇 `navigator.share` ② 有但**唔支援檔案**（iPhone 主畫面 app／部分瀏覽器）
 *   ③ 支援但**用戶手勢過期**（大檔先 download 就過期）／網絡慢。
 *   之前兩輪都係**盲猜**；呢個探針令「你部機支援乜」變成一句事實（`我的 → 關於` 顯示）。
 *
 * ⚠️ 只讀（**唔會**分享任何嘢、唔會發請求）；唔支援就照實 `false`（唔准填假值）。
 * ⚠️ 純函數（注入 `nav`／`win`）→ 可以單元測試，唔靠真瀏覽器。
 */

export interface ShareCapabilities {
  /** 有 `navigator.share`（Web Share API） */
  webShare: boolean;
  /** 可以分享**文字**（title／text） */
  textShare: boolean;
  /** 可以分享**音頻檔案**（mp3）—— 分享首歌本身靠呢個 */
  fileShare: boolean;
  /** 可以分享**圖片**（png）—— 之後做「日記圖片卡」靠呢個 */
  imageShare: boolean;
  /** 係「已安裝 app」（standalone）定瀏覽器分頁 */
  standalone: boolean;
  /** 瀏覽器 UA（截短；出事時幫手認版本） */
  ua: string;
}

type NavLike = {
  share?: unknown;
  canShare?: (data: unknown) => boolean;
  userAgent?: string;
};

type WinLike = {
  matchMedia?: (query: string) => { matches?: boolean } | null;
};

/** 砌一個**假檔案**去問 `canShare`（只需要 type；內容無關） */
function probeFile(name: string, type: string): unknown[] {
  try {
    return [new File([new Uint8Array([0])], name, { type })];
  } catch {
    /** 舊環境冇 `File` → 用 Blob 形狀替代（`canShare` 只睇 type） */
    try {
      return [new Blob([new Uint8Array([0])], { type })];
    } catch {
      return [];
    }
  }
}

export function probeShareCapabilities(nav: NavLike | undefined, win: WinLike | undefined): ShareCapabilities {
  const webShare = Boolean(nav && typeof nav.share === 'function');
  const can = (data: unknown): boolean => {
    if (!nav) return false;
    if (typeof nav.canShare !== 'function') return webShare;
    try {
      return Boolean(nav.canShare(data));
    } catch {
      return false;
    }
  };
  const fileOk = (files: unknown[]): boolean => files.length > 0 && webShare && can({ files });

  return {
    webShare,
    textShare: webShare && can({ title: 'x', text: 'x' }),
    fileShare: fileOk(probeFile('probe.mp3', 'audio/mpeg')),
    imageShare: fileOk(probeFile('probe.png', 'image/png')),
    standalone: Boolean(win?.matchMedia?.('(display-mode: standalone)')?.matches),
    ua: String(nav?.userAgent ?? '').slice(0, 180),
  };
}

/** 一行圖示式摘要（唔需要翻譯：✓／✗ 係通用符號） */
export function formatCapabilities(c: ShareCapabilities): string {
  const mark = (v: boolean) => (v ? '✓' : '✗');
  return `📄${mark(c.fileShare)} 🖼${mark(c.imageShare)} 💬${mark(c.textShare)} 📱${mark(c.standalone)}`;
}

/** 由真瀏覽器讀（唔支援就照實 false） */
export function capabilitiesFromBrowser(): ShareCapabilities {
  const nav = typeof navigator === 'undefined' ? undefined : (navigator as unknown as NavLike);
  const win = typeof window === 'undefined' ? undefined : (window as unknown as WinLike);
  return probeShareCapabilities(nav, win);
}
